import { useMemo } from "react";
import moment from "moment-timezone";
import {
  DataTable,
  type DataTableColumn,
  type DataTableLegendItem,
  type RowBgColorResult,
} from "@/components/ui/DataTable";
import { sum } from "@/components/ui/DataTable/aggregation";
import seedData from "./data/seed-data.json";
import type { SeedRow } from "./data/types";

const TIMEZONE = "America/Chicago";

const data = seedData as SeedRow[];

const columns: DataTableColumn<SeedRow>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    size: 120,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "fullName",
    accessorKey: "fullName",
    header: "Full Name",
    size: 180,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "firstName",
    accessorKey: "firstName",
    header: "First Name",
    size: 140,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "lastName",
    accessorKey: "lastName",
    header: "Last Name",
    size: 140,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "email",
    accessorKey: "email",
    header: "Email",
    size: 220,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "phone",
    accessorKey: "phone",
    header: "Phone",
    size: 160,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "status",
    accessorKey: "status",
    header: "Status",
    size: 150,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "category",
    accessorKey: "category",
    header: "Category",
    size: 140,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "region",
    accessorKey: "region",
    header: "Region",
    size: 130,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "state",
    accessorKey: "state",
    header: "State",
    size: 90,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "tag",
    accessorKey: "tag",
    header: "Tag",
    size: 120,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "amount",
    accessorKey: "amount",
    header: "Amount",
    size: 130,
    cell: (info) =>
      `$${(info.getValue() as number).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    aggregationFn: sum,
    aggregatedCell: (info) =>
      `$${(info.getValue() as number).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
  },
  {
    id: "quantity",
    accessorKey: "quantity",
    header: "Quantity",
    size: 110,
    cell: (info) => (info.getValue() as number).toLocaleString("en-US"),
  },
  {
    id: "score",
    accessorKey: "score",
    header: "Score",
    size: 100,
    cell: (info) => `${info.getValue() as number}`,
  },
  {
    id: "createdDate",
    accessorKey: "createdDate",
    header: "Created Date",
    size: 140,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "updatedDate",
    accessorKey: "updatedDate",
    header: "Updated Date",
    size: 140,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "scheduledAt",
    accessorKey: "scheduledAt",
    header: "Scheduled At",
    size: 200,
    cell: (info) => info.getValue() as string,
  },
  {
    id: "completedAt",
    accessorKey: "completedAt",
    header: "Completed At",
    size: 200,
    cell: (info) => info.getValue() as string,
    columnFilterType: "date",
    filterDateTimezone: TIMEZONE,
    filterDisplayFn: (v) => moment.tz(v as string, TIMEZONE).format("YYYY/MM/DD"),
    filterParseDateFn: (v) => moment.tz(v as string, TIMEZONE),
  },
  {
    id: "isActive",
    accessorKey: "isActive",
    header: "Active",
    size: 90,
    cell: (info) => ((info.getValue() as boolean) ? "Yes" : "No"),
  },
  {
    id: "notes",
    accessorKey: "notes",
    header: "Notes",
    size: 240,
    cell: (info) => info.getValue() as string,
  },
];

const statusToColor: Record<string, RowBgColorResult> = {
  Active: { preset: "light-green" },
  Inactive: { preset: "light-red" },
  Pending: { preset: "light-yellow" },
  Archived: { preset: "light-gray" },
  Suspended: { preset: "light-purple" },
};

const legend: DataTableLegendItem[] = [
  { label: "Active", color: { preset: "light-green" } },
  { label: "Inactive", color: { preset: "light-red" } },
  { label: "Pending", color: { preset: "light-yellow" } },
  { label: "Archived", color: { preset: "light-gray" } },
  { label: "Suspended", color: { preset: "light-purple" } },
];

export function DataTableColoredRowsPage() {
  const memoData = useMemo(() => data, []);

  return (
    <div className="flex flex-col gap-4 p-6 h-screen overflow-hidden">
      <div>
        <h1 className="text-2xl font-bold" data-testid="page-title">
          DataTable Colored Rows Experiment
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          1,000 rows · 20 columns · status-based row colors · virtualized
        </p>
      </div>
      <div className="flex-1 overflow-hidden">
        <DataTable
          data={memoData}
          columns={columns}
          rowBgColor={(row) => statusToColor[row.status]}
          legend={legend}
          enableColumnPinning
          enableMultiSelect
          useSteppedGroupingExpansion
          defaultHideLeafRows
          enableSorting
          enableMultiSort
          enableFiltering
          enableMultiFilter
          filterMode="internal"
          filterStyle="select"
          enableColumnVisibility
          enableVirtualization
          virtualizationRowHeight={40}
          virtualizationOverscan={2}
          stickyHeaders
          enableExport
          enableGrouping
          emptyMessage="No records match the current filters."
          tableClassName="max-h-[calc(100vh-160px)]"
        />
      </div>
    </div>
  );
}
